import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const useGet = ({
  url,
  initialValue,
  params = {},
  auto = true,
  select = (data) => data,
  onSuccess = (data) => {},
  onError = (error) => {},
}) => {
  const [data, setData] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRequestRef = useRef(null);
  const lastPayloadRef = useRef(undefined);
  const paramsRef = useRef(params);
  const selectRef = useRef(select);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    paramsRef.current = params;
    selectRef.current = select;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [params, select, onSuccess, onError]);

  const fetchData = useCallback(async function (requestParams = paramsRef.current, options = {}) {
    const { showLoading = true } = options;

    if (inFlightRequestRef.current) return inFlightRequestRef.current;

    const request = (async () => {
      try {
        if (showLoading) setLoading(true);
        const response = await axios.get(url, { params: requestParams });
        if (response.status === 200) {
          // Polling views refetch every few seconds; an identical payload must
          // not re-render (and restart animations of) the whole list.
          const snapshot = JSON.stringify(response.data);
          if (snapshot !== lastPayloadRef.current) {
            lastPayloadRef.current = snapshot;
            setData(selectRef.current(response.data));
          }
          onSuccessRef.current(response.data);
          setError(null);
        }
      } catch (error) {
        onErrorRef.current(error);
        setError(error);
      } finally {
        if (inFlightRequestRef.current === request) {
          inFlightRequestRef.current = null;
        }
        if (showLoading) setLoading(false);
      }
    })();

    inFlightRequestRef.current = request;
    return request;
  }, [url]);

  useEffect(() => {
    if (auto) fetchData();
  }, [auto, fetchData]);

  return { loading, data, fetchData, error };
};

export default useGet;
